import React, { useEffect } from 'react';
import { BUSINESS_NAME, BUSINESS_PHONE } from '../constants.tsx';

interface Breadcrumb {
  name: string;
  item: string;
}

interface SEOProps {
  title: string;
  description: string;
  breadcrumbs?: Breadcrumb[];
  schemas?: any[]; // Array of JSON-LD schema objects
}

const SEO: React.FC<SEOProps> = ({ title, description, breadcrumbs, schemas }) => {
  useEffect(() => {
    // 1. Update Title
    document.title = title;
    
    // 2. Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // 3. Update Canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const baseUrl = 'https://brisbanecomputerrepair.com';
    let path = window.location.pathname;
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    const cleanUrl = baseUrl + path;

    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', cleanUrl);

    // 4. Update Open Graph URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', cleanUrl);
    }

    // 5. Schema Markup (JSON-LD)
    const existingScript = document.getElementById('dynamic-schema');
    if (existingScript) existingScript.remove();

    // Base Global LocalBusiness Schema for White Hat Entity SEO
    const baseLocalBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "ComputerStore",
      "name": BUSINESS_NAME,
      "image": "https://brisbanecomputerrepair.com/favicon.svg",
      "@id": "https://brisbanecomputerrepair.com/#localbusiness",
      "url": "https://brisbanecomputerrepair.com",
      "telephone": BUSINESS_PHONE,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "5 Grosvenor Rd",
        "addressLocality": "Indooroopilly",
        "addressRegion": "QLD",
        "postalCode": "4068",
        "addressCountry": "AU"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 4.9,
        "reviewCount": 1000,
        "bestRating": 5,
        "worstRating": 1
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -27.5000,
        "longitude": 152.9710
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "08:00",
        "closes": "21:00"
      }
    };

    const allSchemas: any[] = [baseLocalBusinessSchema];

    if (breadcrumbs && breadcrumbs.length > 0) {
      allSchemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.item.startsWith('http') ? crumb.item : `${window.location.origin}${crumb.item}`
        }))
      });
    }

    if (schemas && schemas.length > 0) {
      // Filter out redundant ComputerStore/LocalBusiness schemas if passed manually to prefer our base one 
      // or to merge gracefully
      const filteredSchemas = schemas.filter(s => s["@type"] !== "ComputerStore" && s["@type"] !== "LocalBusiness");
      allSchemas.push(...filteredSchemas);
    }

    if (allSchemas.length > 0) {
      const script = document.createElement('script');
      script.id = 'dynamic-schema';
      script.type = 'application/ld+json';
      
      // Structure all schemas inside a @graph array for better Google parsing
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": allSchemas
      });
      document.head.appendChild(script);
    }

  }, [title, description, breadcrumbs, schemas]);

  return null;
};

export default SEO;